import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Sparkles, TrendingUp, Database, Info, AlertCircle } from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

interface EnrichmentStats {
  total_products: number;
  enriched_products: number;
  avg_original_attrs: number;
  avg_ai_attrs: number;
  avg_total_attrs: number;
  avg_enrichment_pct: number;
}

interface DistributionData {
  ai_attr_range: string;
  product_count: number;
}

interface TopAttribute {
  attr_name: string;
  frequency: number;
}

interface EnrichmentAnalytics {
  summary: EnrichmentStats;
  distribution: DistributionData[];
  topAIAttributes: TopAttribute[];
}

interface ProductEnrichmentDashboardProps {
  siteId: string;
}

export function ProductEnrichmentDashboard({ siteId }: ProductEnrichmentDashboardProps) {
  const [analytics, setAnalytics] = useState<EnrichmentAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;

    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authFetch(`/api/enrichment-analytics/site/${siteId}/coverage`);
        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch enrichment analytics');
        }

        setAnalytics(data.data);
      } catch (err) {
        console.error('Error fetching enrichment analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [siteId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>No enrichment data available for this site.</AlertDescription>
      </Alert>
    );
  }

  const { summary, distribution, topAIAttributes } = analytics;

  // Format distribution data for chart
  const distributionChartData = distribution.map((item) => ({
    range: item.ai_attr_range,
    products: parseInt(item.product_count),
  }));

  // Format top attributes for chart
  const topAttributesChartData = topAIAttributes.slice(0, 10).map((item) => ({
    name: item.attr_name,
    count: parseInt(item.frequency),
  }));

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Products</CardDescription>
            <CardTitle className="text-2xl">{summary.total_products?.toLocaleString() || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Database className="h-3 w-3" />
              In catalog
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Enriched Products</CardDescription>
            <CardTitle className="text-2xl">{summary.enriched_products?.toLocaleString() || 0}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              With AI attributes
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg AI Attributes</CardDescription>
            <CardTitle className="text-2xl">
              {summary.avg_ai_attrs ? parseFloat(summary.avg_ai_attrs.toString()).toFixed(1) : '0'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Per product
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Enrichment Rate</CardDescription>
            <CardTitle className="text-2xl">
              {summary.avg_enrichment_pct ? parseFloat(summary.avg_enrichment_pct.toString()).toFixed(1) : '0'}%
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Overall improvement
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Semantic Enrichment Insight */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Key Insight:</strong> While semantic embeddings don't improve (AI attributes come from descriptions already in the text),
          enrichment provides <strong>structured data</strong> for faceted search, filtering, benefit scoring, and attribute comparisons.
        </AlertDescription>
      </Alert>

      {/* Distribution Chart */}
      <Card>
        <CardHeader>
          <CardTitle>AI Attribute Distribution</CardTitle>
          <CardDescription>
            Number of products by AI-extracted attribute count
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={distributionChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="range" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="products" fill="#3b82f6" name="Products" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top AI Attributes Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Top AI-Extracted Attributes</CardTitle>
          <CardDescription>
            Most frequently extracted attributes across the catalog
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={topAttributesChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={150} />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#8b5cf6" name="Frequency" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Attribute Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Attribute Breakdown</CardTitle>
          <CardDescription>
            Average attributes per product
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="outline">Original</Badge>
                <span className="text-sm text-muted-foreground">From product feed</span>
              </div>
              <span className="text-2xl font-bold">
                {summary.avg_original_attrs ? parseFloat(summary.avg_original_attrs.toString()).toFixed(1) : '0'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-purple-500">AI-Extracted</Badge>
                <span className="text-sm text-muted-foreground">From descriptions</span>
              </div>
              <span className="text-2xl font-bold">
                {summary.avg_ai_attrs ? parseFloat(summary.avg_ai_attrs.toString()).toFixed(1) : '0'}
              </span>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">Total</Badge>
                <span className="text-sm text-muted-foreground">Combined</span>
              </div>
              <span className="text-2xl font-bold">
                {summary.avg_total_attrs ? parseFloat(summary.avg_total_attrs.toString()).toFixed(1) : '0'}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Attributes Table */}
      <Card>
        <CardHeader>
          <CardTitle>All AI-Extracted Attributes</CardTitle>
          <CardDescription>
            Complete list with frequency counts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {topAIAttributes.map((attr, index) => (
              <div key={attr.attr_name} className="flex items-center justify-between p-2 rounded border">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="w-8 justify-center">{index + 1}</Badge>
                  <span className="font-mono text-sm">{attr.attr_name}</span>
                </div>
                <Badge variant="secondary">{attr.frequency} products</Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
